/**
 * Product Spec Selector — W3C ARIA Tabs with Automatic Activation
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-automatic/
 */
if (!customElements.get('product-spec-selector')) {
  customElements.define(
    'product-spec-selector',
    class ProductSpecSelector extends HTMLElement {
      connectedCallback() {
        this.tablist = this.querySelector('[role="tablist"]');
        if (!this.tablist) return;

        this.tabs = Array.from(this.tablist.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        if (!this.tabs.length || !this.panels.length) return;

        this.onTabClick = this.onTabClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
        this.tablist.addEventListener('keydown', this.onKeyDown);

        const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        this.activateTab(activeTab, false);
      }

      disconnectedCallback() {
        if (!this.tabs) return;
        this.tabs.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
        if (this.tablist) {
          this.tablist.removeEventListener('keydown', this.onKeyDown);
        }
      }

      onTabClick(event) {
        this.activateTab(event.currentTarget);
      }

      onKeyDown(event) {
        const currentIndex = this.tabs.indexOf(document.activeElement);
        if (currentIndex === -1) return;

        let nextIndex = currentIndex;

        switch (event.key) {
          case 'ArrowRight':
            nextIndex = (currentIndex + 1) % this.tabs.length;
            break;
          case 'ArrowLeft':
            nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = this.tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        const nextTab = this.tabs[nextIndex];
        nextTab.focus();
        this.activateTab(nextTab);
      }

      activateTab(tab, shouldFocus) {
        if (shouldFocus === undefined) shouldFocus = false;

        this.tabs.forEach((t) => {
          const isActive = t === tab;
          t.classList.toggle('is-active', isActive);
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
          t.tabIndex = isActive ? 0 : -1;

          const panelId = t.getAttribute('aria-controls');
          const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
          if (panel) {
            panel.classList.toggle('is-active', isActive);
            panel.hidden = !isActive;
          }
        });

        if (shouldFocus) {
          tab.focus();
        }
      }
    }
  );
}
